import { useEffect, useState } from "react";
import { toast } from "sonner";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import type { OpenDeal } from "../api";
import { useUpsertDealCommit } from "../api";
import { COMMIT_CATEGORIES, PROBABILITY_PRESETS } from "../constants";
import { fmtCents } from "../format";

/**
 * Per-deal commit drawer — Stan picks a probability (preset button or free-
 * form 0-100%), commit category, and an optional note. Saves to
 * deal_commits; the dashboard's monthly Adj-Amount roll-up reflects the
 * change next refresh.
 */
export function DealCommitDrawer({
  deal,
  forecastYear,
  forecastMonth,
  onOpenChange,
}: {
  deal: OpenDeal | null;
  forecastYear: number;
  forecastMonth: number;
  onOpenChange: (open: boolean) => void;
}) {
  const open = deal != null;
  const [expanded, setExpanded] = useState(false);
  const [probability, setProbability] = useState<number>(0.5);
  const [category, setCategory] = useState<string>("most_likely");
  const [note, setNote] = useState<string>("");

  useEffect(() => {
    if (deal) {
      setProbability(deal.commit_probability ?? 0.5);
      setCategory(deal.commit_category ?? "most_likely");
      setNote("");
    }
  }, [deal]);

  const mutation = useUpsertDealCommit();

  function handleSave() {
    if (!deal) return;
    mutation.mutate(
      {
        hubspot_deal_id: deal.hubspot_deal_id,
        rep_id: deal.owner_rep_id ?? null,
        forecast_year: forecastYear,
        forecast_month: forecastMonth,
        probability,
        commit_category: category,
        is_strategic: deal.is_strategic ?? false,
        note: note || null,
      },
      {
        onSuccess: () => {
          toast.success(`Saved commit for ${deal.name}`);
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(`Failed to save: ${err.message}`);
        },
      },
    );
  }

  if (!deal) {
    return (
      <EntityDrawer
        open={false}
        onOpenChange={onOpenChange}
        expanded={expanded}
        onExpandedChange={setExpanded}
        title=""
      >
        {null}
      </EntityDrawer>
    );
  }

  return (
    <EntityDrawer
      open={open}
      onOpenChange={onOpenChange}
      expanded={expanded}
      onExpandedChange={setExpanded}
      title={deal.name}
      subtitle={
        <>
          {deal.stage_name} · {fmtCents(deal.amount_cents)} ·{" "}
          {deal.owner_display_name ?? "—"}
        </>
      }
    >
      <div className="space-y-6 p-6">
        <div className="space-y-2">
          <Label>Probability</Label>
          <div className="flex flex-wrap gap-2">
            {PROBABILITY_PRESETS.map((p) => (
              <Button
                key={p}
                size="sm"
                variant={Math.abs(probability - p) < 0.001 ? "default" : "outline"}
                onClick={() => setProbability(p)}
              >
                {Math.round(p * 100)}%
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Label className="text-xs text-muted-foreground">Custom %</Label>
            <Input
              type="number"
              min={0}
              max={100}
              step={1}
              className="w-24"
              value={Math.round(probability * 100)}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (!Number.isNaN(v)) {
                  setProbability(Math.max(0, Math.min(100, v)) / 100);
                }
              }}
            />
            <span className="text-sm text-muted-foreground">
              ≈ Adj {fmtCents(Math.round((deal.amount_cents ?? 0) * probability))}
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Commit category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMMIT_CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Note</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Why this number? Risks? Next step? (Stan's reasoning per deal)"
          />
        </div>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save commit"}
          </Button>
        </div>
      </div>
    </EntityDrawer>
  );
}
